// The ragdoll lab's view (`?scene=ragdoll`): the uneven ground and the step in the low-poly look, a figure from its
// card on every stand (its motion, then its pose track sampled at the frame's time on the pose layer), each ragdoll
// posed from its bodies (the figure that stood there, carved by its part list), its motes when it dissolves, and a
// fixed camera with a few framings for shots. View only: it reads the sim and never writes it.
import * as THREE from "three";
import { applyPoses, buildFigure, createMotion, createRagdollView, resetTurns, type Attachment, type Figure, type FigureCard, type Motion, type RagdollView } from "@rpg/figure";
import { createLowPolyLook, type LowPolyLook, type LowPolySceneLook, type LowPolyTier } from "@rpg/look";
import { facet, merge, seeded } from "@rpg/meshkit";
import type { Runtime } from "@rpg/runtime";
import type { SpikeContent } from "../content";
import { labGround, STEP, type RagdollLab, type Stand } from "./lab";
import { createMotes, type Motes } from "./motes";

/** A figure on a stand. */
interface Standing {
  fig: Figure;
  motion: Motion;
  card: string;
  prev: { x: number; y: number; z: number };
  curr: { x: number; y: number; z: number };
}
/** A ragdoll's figure. */
interface Fallen {
  fig: Figure;
  view: RagdollView;
  card: string;
  burst: boolean;
}

/** Camera framings for shots: the whole lab, or one stand (and where its ragdoll lies). */
export type Framing = { kind: "overview" } | { kind: "stand"; stand: number; dist?: number; height?: number; side?: number; ahead?: number; lift?: number } | { kind: "at"; target: [number, number, number]; dist: number; height: number; yaw: number };

export interface RagdollView3 {
  look: LowPolyLook;
  render(): void;
  /** Frame the camera. */
  frame(f: Framing): void;
  /** What is drawn: standing figures, ragdoll figures with their carved meshes, motes. */
  report(): { standing: number; ragdolls: { id: string; card: string; carved: number[]; meshes: number }[]; motes: number };
  /** A stand's figure as drawn: each joint's local turn (Euler XYZ), its world up, the pivot and the root. */
  pose(stand: number): { joints: Record<string, [number, number, number]>; up: Record<string, [number, number, number]>; pivot: { position: [number, number, number]; rot: [number, number, number] }; root: [number, number, number]; yaw: number } | null;
}

export function mountRagdollView(o: { runtime: Runtime; renderer: THREE.WebGLRenderer; scene: THREE.Scene; camera: THREE.PerspectiveCamera; content: SpikeContent; lab: RagdollLab; tier: string }): RagdollView3 {
  const { runtime, scene, camera, lab } = o;
  const reg = o.content.registry;
  const sceneLook = reg.get("looks", "valley-day") as unknown as LowPolySceneLook | undefined;
  if (!sceneLook) throw new Error('ragdoll lab: no look "valley-day"');
  const tierId = o.tier === "low" ? "low" : "high";
  const tier = reg.get("tiers", tierId) as unknown as LowPolyTier | undefined;
  if (!tier) throw new Error(`ragdoll lab: no tier "${tierId}"`);
  // the look brings its own sun, fill and sky
  const old: THREE.Object3D[] = [];
  scene.traverse((c) => (c as THREE.Light).isLight && old.push(c));
  for (const l of old) l.removeFromParent();
  scene.background = null;
  const look = createLowPolyLook({ renderer: o.renderer, scene, camera, look: sceneLook, tier, shadowExtent: 8 });

  // ── the ground and the step: palette facets on the look's one material ──
  const ground = buildGround(look);
  scene.add(ground);
  look.track(ground);

  const attachments = new Map<string, Attachment>(reg.rows("attachments").map((a) => [a.id, a as unknown as Attachment]));
  const cards = lab.tables.cards;
  const probe = (x: number, z: number): number | null => lab.groundAt(x, z);
  const make = (card: string): Figure => {
    const c = cards.get(card)!;
    const fig = buildFigure(c, { material: look.surface, colour: look.palette });
    const row = reg.get("cards", card);
    const first = row?.attachments?.[0];
    if (first && c.socket) fig.attach(attachments.get(first.id) ?? null);
    fig.root.name = `figure:${card}`;
    scene.add(fig.root);
    look.track(fig.root, { blob: row?.blob });
    return fig;
  };

  const standing = new Map<number, Standing>();
  const fallen = new Map<string, Fallen>();
  const motes: Motes = createMotes(scene, look.surface);
  const glow = (card: string): THREE.Color => look.palette(card === "wanderer" ? "hero.gold" : (reg.get("cards", card)?.tier ?? "") === "enemy" ? "enemy.glow" : "hero.cream");

  const ensureStanding = (s: Stand): Standing => {
    let f = standing.get(s.entity);
    if (!f) {
      const fig = make(s.card);
      const motion = createMotion(fig, cards.get(s.card)!.motion!, probe);
      const at = { x: s.x, y: s.y, z: s.z };
      f = { fig, motion, card: s.card, prev: at, curr: { ...at } };
      fig.root.position.set(s.x, s.y, s.z);
      fig.root.rotation.y = s.yaw;
      motion.reset();
      standing.set(s.entity, f);
    }
    return f;
  };
  for (const s of lab.state().stands) if (s.alive) ensureStanding(s);

  // each sim step: where each stand's root is (the frame poses between the last two)
  runtime.on(
    "sim",
    () => {
      for (const s of lab.state().stands) {
        const f = standing.get(s.entity);
        if (!f) continue;
        f.prev = f.curr;
        f.curr = { x: s.x, y: s.y, z: s.z };
        if (Math.hypot(f.curr.x - f.prev.x, f.curr.z - f.prev.z) > 1) f.prev = { ...f.curr };
      }
    },
    { order: 1e8, name: "ragdoll lab: stand state" },
  );

  const still = { speed: 0, velocity: { x: 0, y: 0, z: 0 }, grounded: true, jump: false, glide: false };
  runtime.on(
    "late",
    (c) => {
      if (c.mode === "play" && runtime.paused) return;
      const L = lab.state();
      const B = lab.bodies().state();
      const dt = runtime.clock.dt;
      const tFrame = B.t + (c.alpha - 1) * dt;
      const world = lab.collision();
      // ragdolls: the standing figure hands over to its ragdoll's view (a fresh figure after a restore)
      const live = new Set<string>();
      for (const r of B.ragdolls) {
        live.add(r.state.id);
        let f = fallen.get(r.state.id);
        if (!f) {
          const stand = L.stands.find((s) => s.entity === r.entity);
          let fig: Figure;
          const st = stand ? standing.get(stand.entity) : undefined;
          if (st && stand?.ragdoll === r.state.id) {
            fig = st.fig;
            standing.delete(stand.entity);
          } else fig = make(r.card);
          look.untrack(fig.root);
          look.track(fig.root);
          f = { fig, view: createRagdollView(fig, lab.bodies().resolved(r.card)!), card: r.card, burst: false };
          fallen.set(r.state.id, f);
        }
        f.view.update(world, r.state, c.dt);
        if (r.state.phase === "dissolving" && !f.burst) {
          f.burst = true;
          motes.burst(f.view.centres(), 10 + 6 * f.view.centres().length, glow(f.card), Number(r.state.id.slice(2)) * 7919);
        }
      }
      for (const [id, f] of fallen) {
        if (live.has(id)) continue;
        if (!f.burst) motes.burst(f.view.centres(), 10 + 6 * f.view.centres().length, glow(f.card), Number(id.slice(2)) * 7919);
        look.untrack(f.fig.root);
        f.fig.dispose();
        fallen.delete(id);
      }
      // stands: the motion, then the pose layer at the frame's time
      for (const s of L.stands) {
        if (!s.alive) continue;
        const f = ensureStanding(s);
        const a = c.alpha;
        f.fig.root.position.set(f.prev.x + (f.curr.x - f.prev.x) * a, f.prev.y + (f.curr.y - f.prev.y) * a, f.prev.z + (f.curr.z - f.prev.z) * a);
        f.fig.root.rotation.y = s.yaw;
        const sample = lab.bodies().sample(s.entity, s.card, tFrame);
        const n = Math.min(30, Math.max(1, Math.ceil(c.dt * 60 - 1e-6)));
        for (let i = 0; i < n; i++) {
          resetTurns(f.fig);
          f.motion.update(c.dt / n, { ...still, acting: sample.arms });
        }
        applyPoses(f.fig, sample);
      }
      motes.update(Math.min(c.dt, 0.25));
      look.focus(camTarget);
    },
    { order: -15, name: "ragdoll lab: pose" },
  );

  // ── the camera ──
  const camTarget = new THREE.Vector3(0, 0.5, 0);
  const frame = (f: { kind: string } & Partial<{ stand: number; dist: number; height: number; side: number; ahead: number; lift: number; target: [number, number, number]; yaw: number }>): void => {
    let target = new THREE.Vector3(0, 0.5, 0);
    let yaw = 0.62;
    let dist = 8.5;
    let height = 4.2;
    if (f.kind === "stand") {
      const s = lab.state().stands[f.stand ?? 0]!;
      // `ahead` m in front of the figure, `lift` m up; the camera `side` radians round from its front
      const ahead = f.ahead ?? 0;
      target = new THREE.Vector3(s.x + Math.sin(s.yaw) * ahead, s.y + (f.lift ?? 0.7), s.z + Math.cos(s.yaw) * ahead);
      yaw = s.yaw + (f.side ?? Math.PI / 2);
      dist = f.dist ?? 3.6;
      height = f.height ?? 1.3;
    } else if (f.kind === "at") {
      target = new THREE.Vector3(...f.target!);
      dist = f.dist!;
      height = f.height!;
      yaw = f.yaw!;
    }
    camTarget.copy(target);
    camera.position.set(target.x + Math.sin(yaw) * dist, target.y + height, target.z + Math.cos(yaw) * dist);
    camera.lookAt(target);
    camera.fov = 45;
    camera.updateProjectionMatrix();
  };
  frame({ kind: "overview" });
  look.warm();
  addEventListener("resize", () => look.resize(innerWidth, innerHeight));

  return {
    look,
    render: () => look.render(),
    frame: (f) => frame(f as Parameters<typeof frame>[0]),
    report: () => ({
      standing: standing.size,
      ragdolls: [...fallen].map(([id, f]) => {
        let meshes = 0;
        f.fig.root.traverse((x) => void ((x as THREE.Mesh).isMesh && meshes++));
        return { id, card: f.card, carved: f.view.carved.map((m) => m.geometry.getAttribute("position").count / 3), meshes };
      }),
      motes: motes.alive(),
    }),
    pose: (i) => {
      const s = lab.state().stands[i];
      const f = s ? standing.get(s.entity) : undefined;
      if (!s || !f) return null;
      f.fig.root.updateMatrixWorld(true);
      const joints: Record<string, [number, number, number]> = {};
      const up: Record<string, [number, number, number]> = {};
      for (const [id, j] of f.fig.joints) {
        const e = new THREE.Euler().setFromQuaternion(j.quaternion, "XYZ");
        joints[id] = [e.x, e.y, e.z];
        const u = new THREE.Vector3(0, 1, 0).applyQuaternion(j.getWorldQuaternion(new THREE.Quaternion()));
        up[id] = [u.x, u.y, u.z];
      }
      const pe = new THREE.Euler().setFromQuaternion(f.fig.pivot.quaternion, "XYZ");
      const p = f.fig.root.position;
      return { joints, up, pivot: { position: f.fig.pivot.position.toArray() as [number, number, number], rot: [pe.x, pe.y, pe.z] }, root: [p.x, p.y, p.z], yaw: s.yaw };
    },
  };
}

/** The lab's ground (the heightfield's samples as palette facets, past the field's edge too) and the step. */
function buildGround(look: LowPolyLook): THREE.Mesh {
  const rng = seeded(0xb5);
  const N = 40;
  const S = 16;
  const grass = ["grass.a", "grass.b", "grass.c", "grass.d"].map((k) => look.palette(k));
  const pos: number[] = [];
  const col: number[] = [];
  const v = (i: number, j: number): [number, number, number] => {
    const x = -S / 2 + (i / N) * S;
    const z = -S / 2 + (j / N) * S;
    return [x, labGround(x, z), z];
  };
  for (let j = 0; j < N; j++)
    for (let i = 0; i < N; i++) {
      const a = v(i, j);
      const b = v(i + 1, j);
      const c = v(i, j + 1);
      const d = v(i + 1, j + 1);
      for (const tri of [[a, c, b], [b, c, d]] as [number, number, number][][]) {
        const k = grass[Math.floor(rng() * grass.length)]!;
        for (const p of tri) {
          pos.push(...p);
          col.push(k.r, k.g, k.b);
        }
      }
    }
  const terrain = new THREE.BufferGeometry();
  terrain.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  terrain.setAttribute("color", new THREE.Float32BufferAttribute(col, 3));
  terrain.setAttribute("glow", new THREE.Float32BufferAttribute(new Float32Array(pos.length / 3), 1));
  terrain.computeVertexNormals();
  const step = facet(new THREE.BoxGeometry(STEP.hx * 2, STEP.top + 1, STEP.hz * 2).translate(STEP.x, (STEP.top - 1) / 2, STEP.z), { colour: look.palette("stone.a"), jitter: 0, tint: 0.06, rng });
  const mesh = new THREE.Mesh(merge([terrain, step]), look.surface);
  mesh.name = "ragdoll-lab:world";
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

